/**
 * <s-quick-buy> — collection card quick add + quick view.
 *
 * Wraps one product card and its quick-view <dialog>. Any button with [data-quick-add]
 * adds its data-variant-id to the cart through Shopify Standard Actions (same path as
 * s-product-form), then closes the quick view and opens the cart drawer. Without Standard
 * Actions it falls back to the AJAX cart and the cart page.
 *
 * Option radios inside the quick view live in the collection filter form's DOM, so they are
 * detached with form="…" and their input/change events are stopped here; otherwise
 * <s-live-form> would treat a license click as a filter change.
 *
 * Data:
 *   data-product-id, data-added-label, data-adding-label, data-add-label,
 *   data-sold-out-label, data-unavailable-label, data-error-message, data-cart-add-url, data-cart-url
 *   <script type="application/json" data-variants> [{ id, available, price, options[] }]
 */
class SQuickBuy extends HTMLElement {
  #ac;
  #variants;
  #timers = new WeakMap();

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const isOption = (target) => target instanceof HTMLInputElement && target.hasAttribute('data-option-index');

    this.addEventListener('input', (event) => {
      if (isOption(event.target)) event.stopPropagation();
    }, opts);

    this.addEventListener('change', (event) => {
      if (!isOption(event.target)) return;
      event.stopPropagation();
      this.#sync();
    }, opts);

    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-quick-add]') : null;
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.#add(button);
    }, opts);

    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #variantList() {
    if (!this.#variants) {
      const source = this.querySelector('script[data-variants]');
      try {
        this.#variants = source ? JSON.parse(source.textContent || '[]') : [];
      } catch {
        this.#variants = [];
      }
    }
    return this.#variants;
  }

  #sync() {
    const groups = [...this.querySelectorAll('[data-option-group]')];
    if (!groups.length) return;

    const selected = groups.map((group) => group.querySelector('input[data-option-index]:checked')?.value);
    const variant = this.#variantList.find((item) => item.options.every((value, index) => value === selected[index]));
    const button = this.querySelector('[data-quick-view-add]');
    const price = this.querySelector('[data-quick-view-price]');

    if (variant && price) price.textContent = variant.price;
    if (!button) return;

    const label = button.querySelector('[data-label]');
    if (variant) button.dataset.variantId = String(variant.id);

    const available = Boolean(variant?.available);
    button.disabled = !available;
    button.setAttribute('aria-disabled', String(!available));
    if (label) {
      if (available) label.textContent = this.dataset.addLabel || '';
      else label.textContent = variant ? this.dataset.soldOutLabel || '' : this.dataset.unavailableLabel || '';
    }
  }

  async #add(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const variantId = String(button.dataset.variantId || '').trim();
    if (!variantId) return;

    const error = button.closest('[data-quick-scope]')?.querySelector('[data-quick-error]');
    if (error) error.textContent = '';

    const label = button.querySelector('[data-label]');
    const originalLabel = label?.textContent;
    button.setAttribute('aria-busy', 'true');
    if (label && this.dataset.addingLabel) label.textContent = this.dataset.addingLabel;

    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart(
          { lines: [{ merchandiseId: variantId, quantity: 1 }] },
          { event: { context: 'collection', detail: { source: 's-quick-buy', productId: this.dataset.productId } } }
        );
        const message = result?.userErrors?.[0]?.message;
        if (message) throw new Error(message);

        this.#confirm(button, label, originalLabel);
        this.querySelector('dialog[open]')?.close();
        await window.Shopify.actions.openCart?.();
        return;
      }

      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || '');
      }
      this.#confirm(button, label, originalLabel);
      window.location.href = this.dataset.cartUrl || '/cart';
    } catch (err) {
      if (label && originalLabel != null) label.textContent = originalLabel;
      const message = err?.cause?.userErrors?.[0]?.message || err?.message || this.dataset.errorMessage || '';
      if (error) error.textContent = message;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  #confirm(button, label, originalLabel) {
    if (!label) return;
    label.textContent = this.dataset.addedLabel || originalLabel || '';
    clearTimeout(this.#timers.get(button));
    this.#timers.set(button, setTimeout(() => {
      label.textContent = originalLabel ?? '';
    }, 2200));
  }
}

if (!customElements.get('s-quick-buy')) customElements.define('s-quick-buy', SQuickBuy);
